import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faFilter } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Collapse, Divider, OutlinedInput, Pagination, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider, ptBR } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import moment from 'moment';
import { ChangeEvent, useEffect, useState } from 'react';
import FilterSelect from '../../../components/FilterSelect';
import LoadingApp from '../../../components/LoadingApp';
import { FilterInterface } from '../../../interfaces/entities/FilterInterface';
import { Guia } from '../../../interfaces/entities/Guia';
import { getMinhasGuias } from '../../../services/MinhasGuiasService';
import { sxButtonText } from '../../Login/sxProps';
import { sxPagination } from '../../SaudeOnline/MeusAgendamentos/sxProps';
import MyGuidesCard from './MyGuidesCard';
import './styles.css';
import { sxButton, sxInput, sxLabel } from './sxProps';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import { sxPaginationContrast } from '../../../assets/styles/CommonSxProps';
import { scrollTo } from '../../../utils/ScrollUtil';
import PageTitle from '../../../components/Accessibility/PageTitle';
import DateInput from '../../../components/DateInput';

function MinhasGuias() {
    dayjs.locale('pt-br');
    const [myGuides, setMyGuides] = useState<Guia[]>([]);
    const [myGuidesFiltered, setMyGuidesFiltered] = useState<Guia[]>([]);
    const [showLoading, setShowLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(1);
    const [statusSelected, setStatusSelected] = useState<string>("");
    const [statusGuide, setStatusGuide] = useState<any[]>([]);
    const [eraseFilters, setEraseFilters] = useState<boolean>(false);
    const [showFilters, setShowFilters] = useState<boolean>(true);
    const [dataInicio, setDataInicio] = useState<Dayjs | null>();
    const [dataFim, setDataFim] = useState<Dayjs | null>();
    const [textToFilter, setTextToFilter] = useState<string>('');

    const accessibility = useAccessibility();

    useEffect(() => {
        const getMyGuides = async () => {
            const result = await getMinhasGuias();
            if (result !== null) {
                setMyGuidesFiltered(result.sort(sortByDate))
                setMyGuides(result.sort(sortByDate))
                getStatusGuid(result.sort(sortByDate))
                setShowLoading(false);
            }
        }

        setShowLoading(true);
        getMyGuides();
    }, []);

    const sortByDate = (a: Guia, b: Guia) => {
        let dataA = new Date(a.emissao.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")).getTime();
        let dataB = new Date(b.emissao.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")).getTime();
        return dataB - dataA;
    }

    const paginatedGuides = myGuidesFiltered.slice(
        (page - 1) * 5,
        page * 5
    );

    const handlePageChange = (event: ChangeEvent<unknown>, newPage: number) => {
        setPage(newPage);
        scrollTo('scroll-ref');
    };

    const handleClearFilters = () => {
        setDataFim(null);
        setDataInicio(null);
        setStatusSelected('');
        setEraseFilters(!eraseFilters);
        setMyGuidesFiltered(myGuides);
        setTextToFilter('');
    };

    const stringToDate = (date: string): Date => {
        const partes = date.split('/');
        const dia = parseInt(partes[0], 10);
        const mes = parseInt(partes[1], 10) - 1;
        const ano = parseInt(partes[2], 10);
        return new Date(ano, mes, dia);
    }

    const filter = () => {
        setShowLoading(true);
        setPage(1);
        if ((!dataInicio && !dataFim) && !statusSelected) {
            setShowLoading(false);
            return;
        }
        var filteredMyGuides: Guia[] = [];
        if (statusSelected) {
            filteredMyGuides = filteredMyGuides.length > 0 ? filteredMyGuides : myGuides
            filteredMyGuides = filteredMyGuides.filter(g => g.status_portal === statusSelected)
        }
        if (dataInicio && dataFim) {
            filteredMyGuides = filteredMyGuides.length > 0 ? filteredMyGuides : myGuides
            filteredMyGuides = filteredMyGuides.filter(g => stringToDate(g.emissao) >= dataInicio.toDate() && stringToDate(g.emissao) <= dataFim.toDate())
        }
        setMyGuidesFiltered(filteredMyGuides);
        setShowLoading(false);
    }


    const handleStatus = (value: string) => {
        setStatusSelected(value);
    }

    const getStatusGuid = (guides: Guia[]) => {
        const statusGuide: FilterInterface[] = [];
        guides.forEach((g, index) => {
            var descricao = g.status.trim() === "EM ANALISE" || g.status.trim() === "AUTORIZADA" ? g.status.trim() : "ENTRAR EM CONTATO";
            guides[index].status_portal = descricao;
            descricao = descricao || g.status.replace(/\n/g, '')
            const tipoExistente = statusGuide.some(item => item.descricao === descricao);
            if (!tipoExistente) {
                statusGuide.push({ descricao: descricao, id: descricao });
            }
        });

        setStatusGuide(statusGuide);
    };

    const reloadGuidesData = async () => {
        setShowLoading(true);
        const result = await getMinhasGuias();
        if (result !== null) {
            setMyGuidesFiltered(result.sort(sortByDate))
            setMyGuides(result.sort(sortByDate))
            getStatusGuid(result.sort(sortByDate))
        }
        setShowLoading(false);
    };

    const filterText = (textoDigitado: string) => {
        setTextToFilter(textoDigitado);
        if (!textoDigitado) {
            setMyGuidesFiltered(myGuides);
            return [];
        }

        const textoLowerCase = textoDigitado.toLowerCase();

        var filteredMyGuides = myGuides.filter(guia => {
            for (const prop in guia) {
                if (Object.prototype.hasOwnProperty.call(guia, prop)) {
                    const propriedade = guia[prop as keyof Guia];
                    if (propriedade && propriedade.toString().toLowerCase().includes(textoLowerCase)) {
                        return true;
                    }
                }
            }
            return false;
        });

        setMyGuidesFiltered(filteredMyGuides);
        setPage(1);
    }

    const handleDateChangeIni = (date: Dayjs | null) => {
        if (date !== null) {
            setDataInicio(date)
        }
    }
    const handleDateChangeFim = (date: Dayjs | null) => {
        if (date !== null) {
            setDataFim(date)
        }
    }

    return (
        <div className='my-guides-content'>
            <LoadingApp showLoading={showLoading} />
            <div id='scroll-ref' />
            <PageTitle text="Minhas guias emitidas" />
            <p className='my-guides-instructions' style={accessibility.textStyleAccessibility}>
                Verifique abaixo as guias emitidas nos últimos 365 dias e que ainda não foram executadas.
                Atente-se ao status e validade de cada guia, apenas as válidas e autorizadas podem ser utilizadas.
                Guias de procedimentos já realizados não são exibidas. Para consultar e revalidar guias vencidas, clique em “Revalidação de Guias”.
            </p>
            <Divider />
            <div className='filter-holder-guide'>
                <h1 className='filter-title' style={accessibility.textStyleAccessibility}><FontAwesomeIcon icon={faFilter as IconDefinition} />&nbsp;Filtrar resultados</h1>
                <div className="filter-row-guide">
                    <div className="filter-option-guide">
                        <Typography sx={sxLabel} style={accessibility.textStyleAccessibility}>Filtrar por número, solicitante ou data da guia</Typography>
                        <label htmlFor='filtrar' aria-label='Filtrar por número, solicitante ou data da guia' />
                        <OutlinedInput
                            id='filtrar'
                            aria-describedby='errUserName'
                            fullWidth
                            type={'text'}
                            placeholder='Exemplos: 120587 (número), José (solicitante) ou 14/11/2024 (data)'
                            name='userName'
                            maxRows={1}
                            inputProps={{ maxLength: 100 }}
                            onChange={(e) => filterText(e.target.value)}
                            style={{...accessibility.contrastStyle, ...accessibility.textStyleAccessibility }}
                            value={textToFilter}
                            sx={sxInput}
                        />
                    </div>
                </div>
                <Collapse in={showFilters}>
                    <div className="filter-row-guide">
                        <div className="filter-option-guide-input">
                            <FilterSelect title='Status' value={statusSelected} options={statusGuide} disabled={false} handleChange={handleStatus} erase={eraseFilters} />
                        </div>
                        <div className="filter-option-guide-input">
                            <Typography style={accessibility.textStyleAccessibility} sx={sxLabel}>Data Início</Typography>
                            <DateInput
                                label='Data início'
                                maxDate={dayjs(moment().format('YYYY-MM-DD'))}
                                minDate={dayjs("1900-01-01")}
                                onChange={handleDateChangeIni}
                                value={dataInicio}
                            />
                            
                        </div>
                        <div className="filter-option-guide-input">
                            <Typography style={accessibility.textStyleAccessibility} sx={sxLabel}>Data Fim</Typography>
                            <DateInput
                                label='Data fim'
                                maxDate={dayjs(moment().format('YYYY-MM-DD'))}
                                minDate={dayjs("1900-01-01")}
                                onChange={handleDateChangeFim}
                                value={dataFim}
                            />
                        </div>
                        <div className="clean-filter-button-holder-guide">
                            <Button sx={sxButton} variant="contained" onClick={() => filter()} style={accessibility.buttonStyleAccessibility}>
                                <Typography style={accessibility.buttonTextStyleAccessibility}>Filtrar</Typography>
                            </Button>
                            <Button onClick={handleClearFilters}>
                                <Typography style={accessibility.textStyleAccessibility} sx={sxButtonText}>Limpar filtros</Typography>
                            </Button>
                        </div>
                    </div>
                </Collapse>
            </div>
            <Divider />
            {myGuidesFiltered.length === 0 &&
                <h1 className='empty-notifications' style={accessibility.textStyleAccessibility}>Nenhuma guia encontrada.</h1>
            }
            {myGuidesFiltered.length > 0 &&
                <div className="doctors-cards-list">
                    {paginatedGuides.map((guide, index) => (
                        <MyGuidesCard guide={guide} key={`${guide.idSolic}-${index}`} onDataUpdate={reloadGuidesData} />
                    ))}
                </div>
            }
            {myGuidesFiltered.length > 5 &&
                <div className='pagination-holder'>
                    <Pagination
                        count={Math.ceil(myGuidesFiltered.length / 5)}
                        sx={accessibility.contrastMode ? sxPaginationContrast : sxPagination} hideNextButton hidePrevButton
                        color='primary'
                        page={page}
                        onChange={handlePageChange}
                    />
                </div>
            }
        </div>
    )
}

export default MinhasGuias;